import React from 'react';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Badge } from '@oysterjs/ui/badge';

import { WordDocumentTemplateFieldsFragment, WordDocumentTemplateState } from '~/api/graphql';

import {
  CreatedByCell,
  EditLinkCell,
  TitleCell,
  UpdatedAtCell,
  selectionColumn
} from '~/components/list/library-table-cells';

export type WordTemplateSortKey = 'title' | 'user' | 'date' | 'state';

const EDIT_BASE = '/app/library/word-templates';

const STATE_COLOR: Record<WordDocumentTemplateState, 'green' | 'yellow' | 'red' | 'neutral'> = {
  [WordDocumentTemplateState.Ready]: 'green',
  [WordDocumentTemplateState.Queued]: 'yellow',
  [WordDocumentTemplateState.Processing]: 'yellow',
  [WordDocumentTemplateState.Failed]: 'red'
};

const DISABLED_EDIT_TITLE =
  'This word template cannot be edited while it is processing. Try again when the status is Ready or Failed.';

/**
 * A word template is editable in the library when ingestion has settled
 * (Ready / Failed). While Queued or Processing the row is shown but not
 * interactive.
 */
export function isWordTemplateEditable(template: WordDocumentTemplateFieldsFragment): boolean {
  const state = template.state;
  if (!state) {
    return true;
  }
  return (
    state !== WordDocumentTemplateState.Queued && state !== WordDocumentTemplateState.Processing
  );
}

/** rowHref helper: returns the edit URL only for editable templates. */
export function wordTemplateRowHref(
  template: WordDocumentTemplateFieldsFragment
): string | undefined {
  return isWordTemplateEditable(template) ? `${EDIT_BASE}/${template.id}` : undefined;
}

export function getWordTemplateColumns(opts: {
  selectedSet: ReadonlySet<string>;
  setSelectedWordTemplates: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: WordDocumentTemplateFieldsFragment[];
  /**
   * Whether the caller may change these rows. False drops the bulk-selection
   * column and turns the row link from "Edit" into "View".
   */
  canEdit?: boolean;
}): ColumnDef<WordDocumentTemplateFieldsFragment, WordTemplateSortKey, never>[] {
  const { selectedSet, setSelectedWordTemplates, filtered } = opts;
  return [
    ...(opts.canEdit === false
      ? []
      : [
          selectionColumn<WordDocumentTemplateFieldsFragment, WordTemplateSortKey>({
            selectedSet,
            setSelected: setSelectedWordTemplates,
            filtered,
            pluralLabel: 'word templates'
          })
        ]),
    {
      id: 'title',
      header: 'Title',
      sortKey: 'title',
      renderCell: (row) => <TitleCell title={row.title} visibility={row.visibility} />
    },
    {
      id: 'state',
      header: 'Status',
      sortKey: 'state',
      renderCell: (row) => {
        const state = row.state ?? WordDocumentTemplateState.Ready;
        return <Badge color={STATE_COLOR[state] ?? 'neutral'}>{state}</Badge>;
      }
    },
    {
      id: 'user',
      header: 'Created By',
      sortKey: 'user',
      renderCell: (row) => <CreatedByCell name={row.createdBy.name} />
    },
    {
      id: 'date',
      header: 'Last Updated',
      sortKey: 'date',
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      renderCell: (row) => <UpdatedAtCell value={row.updatedAt} />
    },
    {
      id: 'actions',
      header: 'Actions',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderCell: (row) => (
        <EditLinkCell
          canEdit={opts.canEdit}
          href={`${EDIT_BASE}/${row.id}`}
          disabledTitle={isWordTemplateEditable(row) ? undefined : DISABLED_EDIT_TITLE}
        />
      )
    }
  ];
}

export function wordTemplateSortFn(
  data: readonly WordDocumentTemplateFieldsFragment[],
  sortKey: WordTemplateSortKey,
  direction: 'asc' | 'desc'
): WordDocumentTemplateFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortKey === 'user') {
      return (a.createdBy.name || '').localeCompare(b.createdBy.name || '');
    }
    if (sortKey === 'state') {
      return (a.state ?? '').localeCompare(b.state ?? '');
    }
    const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return aDate - bDate;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}
